import { useCallback, useEffect, useRef, useState } from 'react';
import {
  API_BASE,
  normalizeSchoolAssetPath,
  resolveUploadAssetDisplayUrl,
} from '../utils/api';
import { resolveSchoolAssets, schoolAssetDisplayUrl } from '../utils/schoolIdCardAssets';

/** UI / upload tile keys → school DB column */
/** School logo hydrates from `school.logo` (uploaded in School Information). */
export const ID_CARD_ASSET_FIELDS = {
  schoolLogo: 'logo',
  principalSignature: 'principal_signature',
  footerBackground: 'id_card_footer_bg',
};

/** Upload keys → StudentIdCard / HTML builder URL props */
export const ID_CARD_URL_PROPS = {
  schoolLogo: 'logoUrl',
  principalSignature: 'principalSignatureUrl',
  footerBackground: 'footerBgUrl',
  studentPhoto: 'photoUrl',
};

const EMPTY_DISPLAY = {
  logoUrl: '',
  principalSignatureUrl: '',
  footerBgUrl: '',
  photoUrl: '',
};

function storageKey(schoolId) {
  return `id_card_asset_paths_${schoolId || 'none'}`;
}

function readStoredPaths(schoolId) {
  try {
    const raw = localStorage.getItem(storageKey(schoolId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStoredPaths(schoolId, paths) {
  try {
    localStorage.setItem(storageKey(schoolId), JSON.stringify(paths));
  } catch {
    /* quota */
  }
}

function resolvedToDisplay(resolved, extra = {}) {
  return {
    logoUrl: resolved.logoUrl || '',
    principalSignatureUrl: resolved.principalSignatureUrl || '',
    footerBgUrl: resolved.footerBgUrl || '',
    photoUrl: extra.photoUrl || '',
  };
}

function withCacheBust(url, token) {
  if (!url) return '';
  if (String(url).startsWith('blob:')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${encodeURIComponent(token)}`;
}

/**
 * Centralized live asset URLs for ID card studio + preview.
 * Blob previews on pick; server URLs after upload; localStorage path persistence.
 */
export function useIdCardAssets(schoolId) {
  const [displayUrls, setDisplayUrls] = useState(EMPTY_DISPLAY);
  const [version, setVersion] = useState(0);
  const blobUrlsRef = useRef({});
  const pathsRef = useRef({});

  const bump = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  const revokeBlob = useCallback((assetKey) => {
    const prev = blobUrlsRef.current[assetKey];
    if (prev && String(prev).startsWith('blob:')) {
      URL.revokeObjectURL(prev);
    }
    delete blobUrlsRef.current[assetKey];
  }, []);

  const setDisplayFromMap = useCallback(
    (map, token) => {
      const tokenStr = String(token ?? Date.now());
      setDisplayUrls({
        logoUrl: withCacheBust(map.logoUrl, tokenStr),
        principalSignatureUrl: withCacheBust(map.principalSignatureUrl, tokenStr),
        footerBgUrl: withCacheBust(map.footerBgUrl, tokenStr),
        photoUrl: withCacheBust(map.photoUrl, tokenStr),
      });
      bump();
    },
    [bump]
  );

  /** Hydrate display URLs from school record (+ optional student photo). */
  const hydrateFromSchool = useCallback(
    (school, { photoUrl = '', cacheToken } = {}) => {
      if (!school && !schoolId) {
        setDisplayUrls(EMPTY_DISPLAY);
        bump();
        return;
      }

      const stored = readStoredPaths(schoolId);
      const mergedSchool = { ...school };
      Object.entries(ID_CARD_ASSET_FIELDS).forEach(([, field]) => {
        const fromDb = normalizeSchoolAssetPath(mergedSchool[field], schoolId);
        if (fromDb) {
          mergedSchool[field] = fromDb;
          return;
        }
        const fromStore = normalizeSchoolAssetPath(stored[field], schoolId);
        if (fromStore) mergedSchool[field] = fromStore;
      });

      const token = cacheToken ?? Date.now();
      const resolved = resolveSchoolAssets(mergedSchool, schoolId, token);
      pathsRef.current = { ...stored };
      Object.entries(ID_CARD_ASSET_FIELDS).forEach(([, field]) => {
        const p = normalizeSchoolAssetPath(mergedSchool[field], schoolId);
        if (p) pathsRef.current[field] = p;
      });
      writeStoredPaths(schoolId, pathsRef.current);

      setDisplayUrls((prev) => {
        const base = resolvedToDisplay(resolved, { photoUrl: photoUrl || prev.photoUrl });
        const merged = {
          logoUrl: base.logoUrl || prev.logoUrl || '',
          principalSignatureUrl: base.principalSignatureUrl || prev.principalSignatureUrl || '',
          footerBgUrl: base.footerBgUrl || prev.footerBgUrl || '',
          photoUrl: base.photoUrl || prev.photoUrl || '',
        };
        const tokenStr = String(token);
        return {
          logoUrl: withCacheBust(merged.logoUrl, tokenStr),
          principalSignatureUrl: withCacheBust(merged.principalSignatureUrl, tokenStr),
          footerBgUrl: withCacheBust(merged.footerBgUrl, tokenStr),
          photoUrl: withCacheBust(merged.photoUrl, tokenStr),
        };
      });
      bump();
    },
    [schoolId, bump]
  );

  /** Instant preview before server responds. */
  const setInstantPreview = useCallback(
    (assetKey, file) => {
      if (!file || !file.type?.startsWith('image/')) return null;
      const urlProp = ID_CARD_URL_PROPS[assetKey];
      if (!urlProp) return null;

      revokeBlob(assetKey);
      const blobUrl = URL.createObjectURL(file);
      blobUrlsRef.current[assetKey] = blobUrl;

      setDisplayUrls((prev) => ({
        ...prev,
        [urlProp]: blobUrl,
      }));
      bump();
      return blobUrl;
    },
    [revokeBlob, bump]
  );

  /** After API upload — swap blob → server URL with cache bust. */
  const applyServerPath = useCallback(
    (assetKey, path, uploadData = null) => {
      const field = ID_CARD_ASSET_FIELDS[assetKey];
      const urlProp = ID_CARD_URL_PROPS[assetKey];
      if (!field || !urlProp) return false;

      const canonical = normalizeSchoolAssetPath(path, schoolId);
      const token = Date.now();
      let serverUrl = resolveUploadAssetDisplayUrl(uploadData, schoolId);
      if (!serverUrl && canonical) {
        serverUrl = schoolAssetDisplayUrl(canonical, schoolId, token);
      }

      if (!canonical && !serverUrl) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[useIdCardAssets] Could not build asset URL', { assetKey, path, API_BASE });
        }
        return false;
      }

      revokeBlob(assetKey);
      if (canonical) {
        pathsRef.current = { ...pathsRef.current, [field]: canonical };
        writeStoredPaths(schoolId, pathsRef.current);
      }

      setDisplayUrls((prev) => ({
        ...prev,
        [urlProp]: serverUrl ? withCacheBust(serverUrl, token) : prev[urlProp],
      }));
      bump();
      return Boolean(serverUrl);
    },
    [schoolId, revokeBlob, bump]
  );

  const setStudentPhotoUrl = useCallback(
    (url) => {
      setDisplayUrls((prev) => ({
        ...prev,
        photoUrl: url ? withCacheBust(url, Date.now()) : '',
      }));
      bump();
    },
    [bump]
  );

  const getTilePreviewUrl = useCallback(
    (assetKey) => {
      const urlProp = ID_CARD_URL_PROPS[assetKey];
      return urlProp ? displayUrls[urlProp] || '' : '';
    },
    [displayUrls]
  );

  const cardAssetOverrides = displayUrls;

  useEffect(() => {
    return () => {
      Object.keys(blobUrlsRef.current).forEach((k) => revokeBlob(k));
    };
  }, [revokeBlob]);

  return {
    displayUrls,
    cardAssetOverrides,
    version,
    hydrateFromSchool,
    setInstantPreview,
    applyServerPath,
    setStudentPhotoUrl,
    getTilePreviewUrl,
    bump,
  };
}
